import React, { useEffect, useState, useCallback } from 'react';
import Switch from '@material-ui/core/Switch';
import DataTable from '../../../../components/DataTable';
import Title from '../../../../components/Title';
import handleRequest from '../../../../utils/handle-request';
import { Grid, InputLabel, MenuItem } from '@material-ui/core';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import { makeStyles } from '@material-ui/core/styles';
import Modal from '@material-ui/core/Modal';
import DeleteIcon from '@material-ui/icons/Delete';
import IconButton from '@material-ui/core/IconButton';
import WarningIcon from '@material-ui/icons/Warning';
import { Box } from '@material-ui/core';

const uri = '/service/v1/registered-collaborators';
const initialParams = {
  sort: 'az',
  document_type: '',
  document_number: '',
  status: '',
};

const useStyles = makeStyles((theme) => ({
  avatar: {
    border: '1px solid #0000003b',
    height: 64,
    width: 64,
  },
  formControl: {
    width: '100%',
  },
  button: {
    height: 38.75,
    width: 'fit-content',
    whiteSpace: 'nowrap',
    borderRadius: 4,
    marginLeft: theme.spacing(1),
  },
  modalContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(4),
    boxShadow: theme.shadows[5],
    outline: 0,
    width: '80%',
    maxWidth: 500,
    borderRadius: 8,
  },
  modalTitle: {
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
  },
  modalActions: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  deleteColumn: {
    width: '80px',
    textAlign: 'center',
  },
  deleteIcon: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  warningIcon: {
    fontSize: 120,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(1),
  },
  deleteModalContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(3),
    boxShadow: theme.shadows[5],
    outline: 0,
    width: '90%',
    maxWidth: 400,
    borderRadius: 8,
    textAlign: 'center',
  },
  deleteModalTitle: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    fontSize: '1.5rem',
    fontWeight: 500,
  },
  deleteModalText: {
    marginBottom: theme.spacing(2),
    textAlign: 'left',
    '& ul': {
      paddingLeft: theme.spacing(3),
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    '& li': {
      marginBottom: theme.spacing(0.5),
    }
  },
  deleteModalActions: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    '& button': {
      minWidth: 100,
    }
  },
  deleteWarningText: {
    fontWeight: 500,
    color: theme.palette.error.main,
    marginTop: theme.spacing(2),
    marginBottom: 0,
  },
}));

function Register() {
  const [params, setParams] = useState(initialParams);
  const [localStatuses, setLocalStatuses] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isStatusUpdateModalOpen, setIsStatusUpdateModalOpen] = useState(false);
  const [statusUpdateMessage, setStatusUpdateMessage] = useState('');
  const [filter, setFilter] = useState({
    documentType: '',
    documentNumber: '',
    status: '',
  });
  const [companies, setCompanies] = useState([]);
  const [refreshRow, setRefreshRow] = useState(null);
  const [isNoCompanyModalOpen, setIsNoCompanyModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const classes = useStyles();
  const theme = useStyles().theme;

  const translateActivationStatus = (status) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'auto':
        return 'Automático';
      case 'manual':
        return 'Manual';
      default:
        return status;
    }
  };

  useEffect(() => {
    async function fetchCompanies() {
      const response = await handleRequest('get', '/service/v1/company/get-all');
      if (response.success) {
        setCompanies(response.data);
      }
    }
    fetchCompanies();
  }, []);

  function changeFilter(event) {
    const { name, value } = event.target;
    setFilter((prevFilter) => ({
      ...prevFilter,
      [name]: value,
    }));
  }

  function handleFilter() {
    setParams({
      sort: 'az',
      document_type: filter.documentType || '',
      document_number: filter.documentNumber || '',
      status: filter.status || '',
      page: 1,
      per_page: 10,
    });
  }

  function handleReset() {
    setFilter({
      documentType: '',
      documentNumber: '',
      status: '',
    });
    setParams(initialParams);
  }

  const handleRowRefresh = useCallback((documentNumber) => {
    setRefreshRow(documentNumber);
  }, []);

  async function handleStatusChange(documentNumber, newStatus, row) {
    const oldStatus = localStatuses[documentNumber] !== undefined ? localStatuses[documentNumber] : newStatus === 0;

    if (newStatus === 1 && !row.company) {
      setSelectedUser(row);
      setIsCompanyModalOpen(true);
    } else {
      try {
        const response = await handleRequest('put', '/service/v1/collaborator/update-status', {
          document_number: documentNumber,
          status: newStatus === 1,
        });

        if (!response.success) {
          console.error('Error updating status:', response.error);
          setStatusUpdateMessage(`Error al actualizar el estado: ${response.error}`);
          setIsStatusUpdateModalOpen(true);
          setLocalStatuses((prevStatuses) => ({
            ...prevStatuses,
            [documentNumber]: oldStatus,
          }));
        } else {
          setStatusUpdateMessage('El estado del usuario se ha actualizado correctamente');
          setIsStatusUpdateModalOpen(true);
          handleRowRefresh(documentNumber);
          setLocalStatuses((prevStatuses) => ({
            ...prevStatuses,
            [documentNumber]: newStatus === 1,
          }));
        }
      } catch (error) {
        console.error('Error updating status:', error);
        setStatusUpdateMessage(`Error al actualizar el estado: ${error.message}`);
        setIsStatusUpdateModalOpen(true);
        setLocalStatuses((prevStatuses) => ({
          ...prevStatuses,
          [documentNumber]: oldStatus,
        }));
      }
    }
  }

  function handleCompanySelectionChange(event) {
    setSelectedCompany(event.target.value);
  }

  function confirmCompanyAssignment() {
    if (selectedCompany) {
      setIsConfirmationModalOpen(true);
    } else {
      alert('Please select a company before confirming.');
    }
  }

  async function assignCompanyToUser() {
    try {
      const response = await handleRequest('put', '/service/v1/user/set-company', {
        document_number: selectedUser.document_number,
        company_id: companies.find((company) => company.name === selectedCompany).id_company,
      });

      if (!response.success) {
        console.error('Error assigning company:', response.error);
        setStatusUpdateMessage(`Error al asignar la empresa: ${response.error}. No se puede activar el estado.`);
        setIsStatusUpdateModalOpen(true);
        setLocalStatuses((prevStatuses) => ({
          ...prevStatuses,
          [selectedUser.document_number]: false,
        }));
        handleRowRefresh(selectedUser.document_number);
      } else {
        const statusResponse = await handleRequest('put', '/service/v1/collaborator/update-status', {
          document_number: selectedUser.document_number,
          status: true,
        });
        if (statusResponse.success) {
          setStatusUpdateMessage('La empresa se ha asignado correctamente y el estado del usuario se ha actualizado');
          setIsStatusUpdateModalOpen(true);
          setLocalStatuses((prevStatuses) => ({
            ...prevStatuses,
            [selectedUser.document_number]: true,
          }));
          handleRowRefresh(selectedUser.document_number);
        } else {
          setStatusUpdateMessage('La empresa se ha asignado correctamente, pero el estado del usuario no se ha actualizado');
          setIsStatusUpdateModalOpen(true);
          setLocalStatuses((prevStatuses) => ({
            ...prevStatuses,
            [selectedUser.document_number]: false,
          }));
          handleRowRefresh(selectedUser.document_number);
        }
      }
    } catch (error) {
      console.error('Error assigning company:', error);
      setStatusUpdateMessage(`Error al asignar la empresa: ${error.message}. No se puede activar el estado.`);
      setIsStatusUpdateModalOpen(true);
      setLocalStatuses((prevStatuses) => ({
        ...prevStatuses,
        [selectedUser.document_number]: false,
      }));
      handleRowRefresh(selectedUser.document_number);
    } finally {
      setIsConfirmationModalOpen(false);
      setIsCompanyModalOpen(false);
      setSelectedCompany('');
      setSelectedUser(null);
    }
  }

  async function finalizeCompanyAssignment() {
    if (selectedUser && selectedCompany) {
      await assignCompanyToUser();
      setIsConfirmationModalOpen(false);
      setIsCompanyModalOpen(false);
      setSelectedCompany('');
    }
  }

  function handleCompanyModalClose() {
    setIsCompanyModalOpen(false);
    setIsNoCompanyModalOpen(true);
    setLocalStatuses((prevStatuses) => ({
      ...prevStatuses,
      [selectedUser.document_number]: false,
    }));
    handleRowRefresh(selectedUser.document_number);
  }

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await handleRequest(
        'delete', 
        `/service/v1/collaborator/delete/${userToDelete.document_number}`
      );

      if (response.success) {
        setStatusUpdateMessage('Usuario eliminado correctamente');
        setIsStatusUpdateModalOpen(true);
        setParams(prevParams => ({
          ...prevParams,
          refresh: Date.now()
        }));
      } else {
        setStatusUpdateMessage(`Error al eliminar usuario: ${response.data.errorMessageDetail}`);
        setIsStatusUpdateModalOpen(true);
      }
    } catch (error) {
      setStatusUpdateMessage(`Error al eliminar usuario ${error.message ? error.message : ''}`);
      setIsStatusUpdateModalOpen(true);
    } finally {
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    }
  };

  const tableHeaders = [
    {
      name: 'company',
      label: 'Sociedad',
    },
    {
      name: 'document_type',
      label: 'Tipo documento',
    },
    {
      name: 'document_number',
      label: 'Nro. documento',
    },
    {
      name: 'names',
      label: 'Nombre',
    },
    {
      name: 'last_name',
      label: 'Apellido',
    },
    {
      name: 'status',
      label: 'Estado',
      component: (value, row) => (
        <Switch
          checked={localStatuses.hasOwnProperty(row.document_number) ? localStatuses[row.document_number] : value === 1}
          onChange={(event) => handleStatusChange(row.document_number, event.target.checked ? 1 : 0, row)}
          color="primary"
        />
      ),
    },
    {
      name: 'activation',
      label: 'Activación',
      component: (value) => translateActivationStatus(value),
    },
    {
      name: 'actions',
      label: 'Eliminar',
      className: classes.deleteColumn,
      component: (value, row) => (
        <div className={classes.deleteIcon}>
        <IconButton
          onClick={() => handleDeleteClick(row)}
          color="secondary"
          size="small"
        >
          <DeleteIcon />
        </IconButton>
        </div>
      ),
    }
  ];

  return (
    <>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <FormControl margin="dense" size="small" className={classes.formControl}>
            <InputLabel>Tipo de documento</InputLabel>
            <Select name="documentType" value={filter.documentType} onChange={changeFilter}>
              <MenuItem value="1">DNI</MenuItem>
              <MenuItem value="2">CE</MenuItem>
              <MenuItem value="3">PAS</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Número de documento"
            name="documentNumber"
            value={filter.documentNumber}
            onChange={changeFilter}
            fullWidth
            size="small"
            margin="dense"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl margin="dense" size="small" className={classes.formControl}>
            <InputLabel>Estado</InputLabel>
            <Select name="status" value={filter.status} onChange={changeFilter}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="0">Inactivo</MenuItem>
              <MenuItem value="1">Activo</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Button onClick={handleFilter} label="Filtrar" variant="contained" color="primary" className={classes.button}>
            Filtrar
          </Button>
          <Button
            onClick={handleReset}
            label="Reiniciar"
            variant="outlined"
            color="secondary"
            className={classes.button}
          >
            Reiniciar
          </Button>
        </Grid>
      </Grid>
      <br />
      <DataTable headers={tableHeaders} uri={uri} params={params} setParams={setParams} refreshRow={refreshRow} onRowRefreshed={() => setRefreshRow(null)} />
      <Modal open={isCompanyModalOpen} onClose={handleCompanyModalClose}>
        <div className={classes.modalContent}>
          <h2 className={classes.modalTitle}>Seleccionar una Empresa</h2>
          <FormControl margin="dense" size="small" className={classes.formControl}>
            <InputLabel>Empresa</InputLabel>
            <Select value={selectedCompany} onChange={handleCompanySelectionChange}>
              {companies.map((company) => (
                <MenuItem key={company.id_company} value={company.name}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div className={classes.modalActions}>
            <Button
              onClick={confirmCompanyAssignment}
              label="Confirmar"
              variant="contained"
              color="primary"
              className={classes.button}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </Modal>
      <Modal open={isConfirmationModalOpen} onClose={() => setIsConfirmationModalOpen(false)}>
        <div className={classes.modalContent}>
          <h2 className={classes.modalTitle}>Confirmar Asignación</h2>
          <p>
            {selectedCompany && selectedUser
              ? `¿Está seguro de que desea asignar la sociedad ${selectedCompany} al usuario ${selectedUser.names} ${selectedUser.last_name}?`
              : 'No se ha seleccionado usuario o empresa.'}
          </p>
          <div className={classes.modalActions}>
            <Button
              onClick={() => setIsConfirmationModalOpen(false)}
              label="No"
              variant="outlined"
              color="secondary"
              className={classes.button}
            >
              No
            </Button>
            <Button
              onClick={finalizeCompanyAssignment}
              label="Sí"
              variant="contained"
              color="primary"
              className={classes.button}
            >
              Sí
            </Button>
          </div>
        </div>
      </Modal>
      <Modal open={isStatusUpdateModalOpen} onClose={() => setIsStatusUpdateModalOpen(false)}>
        <div className={classes.modalContent}>
          <h2 className={classes.modalTitle}>Estado actualizado</h2>
          <p>{statusUpdateMessage}</p>
          <div className={classes.modalActions}>
            <Button
              onClick={() => setIsStatusUpdateModalOpen(false)}
              variant="outlined"
              color="secondary"
              className={classes.button}
            >
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>
      <Modal open={isNoCompanyModalOpen} onClose={() => setIsNoCompanyModalOpen(false)}>
        <div className={classes.modalContent}>
          <h2 className={classes.modalTitle}>No se puede activar</h2>
          <p>No se ha asignado una compañía al usuario. Por favor, asigne una compañía antes de activar el estado.</p>
          <div className={classes.modalActions}>
            <Button
              onClick={() => setIsNoCompanyModalOpen(false)}
              variant="outlined"
              color="secondary"
              className={classes.button}
            >
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>
      <Modal open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
        <div className={classes.deleteModalContent}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <WarningIcon className={classes.warningIcon} />

          </Box>
          <div className={classes.deleteModalText}>
            <p><strong>Al eliminar la cuenta:</strong></p>
            <ul>
              <li>El usuario perderá acceso a todas las funcionalidades.</li>
              <li>Se eliminarán los datos personales.</li>
              <li>Esta acción es irreversible!</li>
            </ul>
            <p>
              ¿Seguro que desea eliminar la cuenta?
            </p>
          </div>
          <div className={classes.deleteModalActions}>
            <Button
              onClick={() => setIsDeleteModalOpen(false)}
              variant="contained"
              color="primary"
              className={classes.button}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="secondary"
              className={classes.button}
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default Register;
